import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

function Navbar() {
  const { isAuthenticated, user, companyId, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Organização dos itens do menu em grupos lógicos
  const menuGroups = [
    {
      title: 'Principal',
      items: [
        { path: '/dashboard', label: 'Painel', icon: '📊' },
        { path: '/alerts', label: 'Alertas', icon: '🔔' },
      ]
    },
    {
      title: 'Financeiro',
      items: [
        { path: '/revenues', label: 'Receitas', icon: '💰' },
        { path: '/expenses', label: 'Despesas', icon: '💸' },
        { path: '/accounts-receivable', label: 'Contas a Receber', icon: '📥' },
        { path: '/accounts-payable', label: 'Contas a Pagar', icon: '📤' },
        { path: '/transfers', label: 'Transferências', icon: '🔄' },
        { path: '/invoices', label: 'Faturas', icon: '📄' },
      ]
    },
    {
      title: 'Cadastros',
      items: [
        { path: '/banks', label: 'Bancos', icon: '🏦' },
        { path: '/credit-cards', label: 'Cartões', icon: '💳' },
        { path: '/categories', label: 'Categorias', icon: '📁' },
        { path: '/providers', label: 'Fornecedores', icon: '🏢' },
        { path: '/customers', label: 'Clientes', icon: '👥' },
      ]
    },
  ];

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-light">
      <div className="container-fluid">
        <Link className="navbar-brand" to="/" onClick={handleLinkClick}>
          Conta Blue
        </Link>

        {/* Botão Hambúrguer para Mobile */}
        <button
          className={`navbar-toggler ${isMenuOpen ? '' : 'collapsed'}`}
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-controls="navbarNav"
          aria-expanded={isMenuOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarNav">
          {isAuthenticated && (
            <>
              {/* Menu Desktop - Links simples */}
              <ul className="navbar-nav me-auto d-none d-lg-flex">
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`} to="/dashboard">Painel</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/revenues') ? 'active' : ''}`} to="/revenues">Receitas</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/expenses') ? 'active' : ''}`} to="/expenses">Despesas</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/accounts-receivable') ? 'active' : ''}`} to="/accounts-receivable">C. Receber</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/accounts-payable') ? 'active' : ''}`} to="/accounts-payable">C. Pagar</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/transfers') ? 'active' : ''}`} to="/transfers">Transferências</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/invoices') ? 'active' : ''}`} to="/invoices">Faturas</Link>
                </li>

                {/* Dropdown Cadastros - Desktop */}
                <li className="nav-item dropdown">
                  <button
                    className="nav-link dropdown-toggle btn btn-link"
                    onClick={() => toggleDropdown('cadastros')}
                    aria-expanded={openDropdown === 'cadastros'}
                  >
                    Cadastros
                  </button>
                  <ul className={`dropdown-menu ${openDropdown === 'cadastros' ? 'show' : ''}`}>
                    <li><Link className="dropdown-item" to="/banks" onClick={handleLinkClick}>Bancos</Link></li>
                    <li><Link className="dropdown-item" to="/credit-cards" onClick={handleLinkClick}>Cartões de Crédito</Link></li>
                    <li><Link className="dropdown-item" to="/categories" onClick={handleLinkClick}>Categorias</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><Link className="dropdown-item" to="/providers" onClick={handleLinkClick}>Fornecedores</Link></li>
                    <li><Link className="dropdown-item" to="/customers" onClick={handleLinkClick}>Clientes</Link></li>
                  </ul>
                </li>

                {/* Dropdown Relatórios - Desktop */}
                <li className="nav-item dropdown">
                  <button
                    className="nav-link dropdown-toggle btn btn-link"
                    onClick={() => toggleDropdown('relatorios')}
                    aria-expanded={openDropdown === 'relatorios'}
                  >
                    Relatórios
                  </button>
                  <ul className={`dropdown-menu ${openDropdown === 'relatorios' ? 'show' : ''}`}>
                    <li><Link className="dropdown-item" to="/reports" onClick={handleLinkClick}>Visão Geral</Link></li>
                    <li><Link className="dropdown-item" to="/reports/cash-flow" onClick={handleLinkClick}>Fluxo de Caixa</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><Link className="dropdown-item" to="/daily-summary" onClick={handleLinkClick}>Lançamentos do Dia</Link></li>
                  </ul>
                </li>

                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/alerts') ? 'active' : ''}`} to="/alerts">Alertas</Link>
                </li>

                {(user?.company_id === null || user?.is_super_admin) && (
                  <li className="nav-item">
                    <Link className={`nav-link ${isActive('/settings') ? 'active' : ''}`} to="/settings">Configurações</Link>
                  </li>
                )}

                {user?.is_super_admin && user?.company_id === 1 && (
                  <li className="nav-item dropdown">
                    <button
                      className={`nav-link dropdown-toggle btn btn-link ${isActive('/admin') ? 'active' : ''}`}
                      onClick={() => toggleDropdown('admin')}
                      aria-expanded={openDropdown === 'admin'}
                    >
                      Admin
                    </button>
                    <ul className={`dropdown-menu dropdown-menu-end ${openDropdown === 'admin' ? 'show' : ''}`}>
                      <li><Link className="dropdown-item" to="/admin" onClick={handleLinkClick}>Dashboard</Link></li>
                      <li><Link className="dropdown-item" to="/admin/companies" onClick={handleLinkClick}>Clientes</Link></li>
                      <li><Link className="dropdown-item" to="/admin/plans" onClick={handleLinkClick}>Planos</Link></li>
                      <li><Link className="dropdown-item" to="/admin/systems" onClick={handleLinkClick}>Sistemas</Link></li>
                    </ul>
                  </li>
                )}
              </ul>

              {/* Menu Mobile - Organizado em grupos */}
              <div className="d-lg-none mobile-menu">
                {/* Info do usuário no topo do menu mobile */}
                <div className="mobile-user-info">
                  <div className="user-avatar">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="user-details">
                    <span className="user-name">{user?.name}</span>
                    <span className="user-company">Empresa: {companyId}</span>
                  </div>
                </div>

                {menuGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="mobile-menu-group">
                    <div className="mobile-menu-group-title">{group.title}</div>
                    <ul className="navbar-nav">
                      {group.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="nav-item">
                          <Link
                            className={`nav-link mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                            to={item.path}
                            onClick={handleLinkClick}
                          >
                            <span className="nav-icon">{item.icon}</span>
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Relatórios no mobile */}
                <div className="mobile-menu-group">
                  <div className="mobile-menu-group-title">Relatórios</div>
                  <ul className="navbar-nav">
                    <li className="nav-item">
                      <Link className={`nav-link mobile-nav-link ${isActive('/reports') && location.pathname === '/reports' ? 'active' : ''}`} to="/reports" onClick={handleLinkClick}>
                        <span className="nav-icon">📈</span>
                        Visão Geral
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link className={`nav-link mobile-nav-link ${isActive('/reports/cash-flow') ? 'active' : ''}`} to="/reports/cash-flow" onClick={handleLinkClick}>
                        <span className="nav-icon">💹</span>
                        Fluxo de Caixa
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link className={`nav-link mobile-nav-link ${isActive('/daily-summary') ? 'active' : ''}`} to="/daily-summary" onClick={handleLinkClick}>
                        <span className="nav-icon">🗓️</span>
                        Lançamentos do Dia
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Configurações e Backoffice (se admin) */}
                {(user?.company_id === null || user?.is_super_admin) && (
                  <div className="mobile-menu-group">
                    <div className="mobile-menu-group-title">Sistema</div>
                    <ul className="navbar-nav">
                      <li className="nav-item">
                        <Link className={`nav-link mobile-nav-link ${isActive('/settings') ? 'active' : ''}`} to="/settings" onClick={handleLinkClick}>
                          <span className="nav-icon">⚙️</span>
                          Configurações
                        </Link>
                      </li>
                      {user?.is_super_admin && user?.company_id === 1 && (
                        <>
                          <li className="nav-item">
                            <Link className={`nav-link mobile-nav-link ${isActive('/admin') && !isActive('/admin/companies') && !isActive('/admin/plans') && !isActive('/admin/systems') ? 'active' : ''}`} to="/admin" onClick={handleLinkClick}>
                              <span className="nav-icon">🛡️</span>
                              Admin — Dashboard
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link className={`nav-link mobile-nav-link ${isActive('/admin/companies') ? 'active' : ''}`} to="/admin/companies" onClick={handleLinkClick}>
                              <span className="nav-icon">🏢</span>
                              Admin — Clientes
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link className={`nav-link mobile-nav-link ${isActive('/admin/plans') ? 'active' : ''}`} to="/admin/plans" onClick={handleLinkClick}>
                              <span className="nav-icon">📋</span>
                              Admin — Planos
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link className={`nav-link mobile-nav-link ${isActive('/admin/systems') ? 'active' : ''}`} to="/admin/systems" onClick={handleLinkClick}>
                              <span className="nav-icon">🖥️</span>
                              Admin — Sistemas
                            </Link>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                )}

                {/* Botão Logout no mobile */}
                <div className="mobile-menu-footer">
                  <button className="btn btn-outline-danger w-100" onClick={handleLogout}>
                    Sair da Conta
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Menu Desktop - Lado direito (usuário) */}
          <ul className="navbar-nav d-none d-lg-flex">
            {isAuthenticated ? (
              <>
                <li className="nav-item">
                  <span className="nav-link text-muted">
                    {user?.name} | Empresa: {companyId}
                  </span>
                </li>
                <li className="nav-item">
                  <button className="btn btn-outline-primary btn-sm" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li className="nav-item">
                <Link className="nav-link" to="/login" onClick={handleLinkClick}>Login</Link>
              </li>
            )}
          </ul>

          {/* Login para não autenticados em mobile */}
          {!isAuthenticated && (
            <ul className="navbar-nav d-lg-none">
              <li className="nav-item">
                <Link className="nav-link mobile-nav-link" to="/login" onClick={handleLinkClick}>
                  <span className="nav-icon">🔐</span>
                  Login
                </Link>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* Overlay para fechar menu ao clicar fora */}
      {isMenuOpen && (
        <div className="mobile-menu-overlay d-lg-none" onClick={() => setIsMenuOpen(false)} />
      )}
    </nav>
  );
}

export default Navbar;
